import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, qs } from "../api/client";
import { PageHeader } from "../components/Layout";
import { useToast } from "../components/Toast";
import {
  Avatar,
  Badge,
  ConfirmModal,
  EmptyState,
  Field,
  IconDownload,
  IconEdit,
  IconPlus,
  IconSearch,
  IconStar,
  IconTrash,
  IconUsers,
  Modal,
  Pagination,
  Spinner,
  TableSkeleton,
} from "../components/ui";
import { formatDate } from "../utils/format";

const PAGE_SIZE = 25;
const EMPTY_FILTERS = { search: "", industry: "", source: "", featured: "", has_email: "" };

export default function Members() {
  const toast = useToast();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [sort, setSort] = useState({ key: "created_at", direction: "desc" });
  const [page, setPage] = useState(1);

  const [members, setMembers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, pageSize: PAGE_SIZE });
  const [options, setOptions] = useState({ industries: [], sources: [] });
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput }));
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const query = useMemo(
    () => qs({ ...filters, page, page_size: PAGE_SIZE, sort: sort.key, direction: sort.direction }),
    [filters, page, sort]
  );

  // Guards against an older in-flight request overwriting a newer response.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const res = await api.get(`/members${query}`);
      if (id !== requestId.current) return;
      setMembers(res.members);
      setPagination(res.pagination);
    } catch (err) {
      if (id === requestId.current) toast.error(err.message);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toast identity is stable
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get("/members/filters")
      .then(setOptions)
      .catch(() => {
        /* filter dropdowns are a nicety — a failure here shouldn't shout */
      });
  }, []);

  const pageIds = members.map((m) => m.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  function toggleRow(id) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function togglePage() {
    setSelected((current) => {
      const next = new Set(current);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  }

  async function selectAllMatching() {
    try {
      const { ids } = await api.get(`/members/ids${qs(filters)}`);
      setSelected(new Set(ids));
      toast.toast(`Selected all ${ids.length.toLocaleString()} matching members.`);
    } catch (err) {
      toast.error(err.message);
    }
  }

  function changeSort(key) {
    setSort((s) => ({
      key,
      direction: s.key === key && s.direction === "desc" ? "asc" : "desc",
    }));
    setPage(1);
  }

  function applyFilter(patch) {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  }

  async function runBulk(action) {
    const ids = [...selected];
    setBusy(true);
    try {
      if (action === "delete") {
        const { deleted } = await api.post("/members/bulk-delete", { ids });
        toast.success(`Deleted ${deleted.toLocaleString()} member${deleted === 1 ? "" : "s"}.`);
      } else {
        const is_featured = action === "feature";
        const { updated } = await api.post("/members/bulk-feature", { ids, is_featured });
        toast.success(
          `${is_featured ? "Added" : "Removed"} ${updated.toLocaleString()} ${
            is_featured ? "to" : "from"
          } the Spotlight.`
        );
      }
      setSelected(new Set());
      setConfirming(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteOne(member) {
    setBusy(true);
    try {
      await api.del(`/members/${member.id}`);
      toast.success(`Removed ${member.name}.`);
      setConfirming(null);
      setSelected((s) => {
        const next = new Set(s);
        next.delete(member.id);
        return next;
      });
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleFeatured(member) {
    try {
      const { member: updated } = await api.patch(`/members/${member.id}`, {
        is_featured: !member.is_featured,
      });
      setMembers((list) => list.map((m) => (m.id === updated.id ? updated : m)));
      toast.success(
        updated.is_featured ? `${updated.name} added to the Spotlight.` : `${updated.name} removed from the Spotlight.`
      );
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function exportCsv() {
    setExporting(true);
    try {
      await api.download(`/members/export.csv${qs(filters)}`, "members.csv");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  }

  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <>
      <PageHeader
        title="Members"
        subtitle={`${pagination.total.toLocaleString()} ${hasFilters ? "matching" : "total"}`}
      >
        <button type="button" className="btn btn-sm" onClick={exportCsv} disabled={exporting}>
          {exporting ? <Spinner dark /> : <IconDownload />}
          Export CSV
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={() => setCreating(true)}>
          <IconPlus />
          Add member
        </button>
      </PageHeader>

      <div className="content">
        <div className="toolbar" style={{ marginBottom: 16 }}>
          <span className="search-wrap">
            <IconSearch />
            <input
              className="input"
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, company, email, role…"
            />
          </span>

          <select
            className="select"
            style={{ width: "auto" }}
            value={filters.industry}
            onChange={(e) => applyFilter({ industry: e.target.value })}
          >
            <option value="">All industries</option>
            {options.industries.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>

          <select
            className="select"
            style={{ width: "auto" }}
            value={filters.source}
            onChange={(e) => applyFilter({ source: e.target.value })}
          >
            <option value="">All sources</option>
            {options.sources.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <select
            className="select"
            style={{ width: "auto" }}
            value={filters.featured}
            onChange={(e) => applyFilter({ featured: e.target.value })}
          >
            <option value="">Everyone</option>
            <option value="true">On the Spotlight</option>
            <option value="false">Not featured</option>
          </select>

          <select
            className="select"
            style={{ width: "auto" }}
            value={filters.has_email}
            onChange={(e) => applyFilter({ has_email: e.target.value })}
          >
            <option value="">Any contact</option>
            <option value="true">Has email</option>
            <option value="false">No email</option>
          </select>

          {hasFilters && (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => {
                setFilters(EMPTY_FILTERS);
                setSearchInput("");
                setPage(1);
              }}
            >
              Clear
            </button>
          )}
        </div>

        {selected.size > 0 && (
          <div className="bulkbar">
            <span className="bulkbar-count">{selected.size.toLocaleString()} selected</span>
            {allOnPageSelected && selected.size < pagination.total && (
              <button type="button" className="bulkbar-link" onClick={selectAllMatching}>
                Select all {pagination.total.toLocaleString()} matching
              </button>
            )}
            <span className="spacer" />
            <button type="button" className="btn btn-sm" onClick={() => runBulk("feature")} disabled={busy}>
              <IconStar />
              Add to Spotlight
            </button>
            <button type="button" className="btn btn-sm" onClick={() => runBulk("unfeature")} disabled={busy}>
              Remove from Spotlight
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() => setConfirming({ type: "bulk" })}
              disabled={busy}
            >
              <IconTrash />
              Delete
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        )}

        <div className="panel">
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th className="col-check">
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={allOnPageSelected}
                      onChange={togglePage}
                      disabled={!members.length}
                      aria-label="Select all on this page"
                    />
                  </th>
                  <SortHeader label="Name" sortKey="name" sort={sort} onSort={changeSort} />
                  <SortHeader label="Company" sortKey="company" sort={sort} onSort={changeSort} />
                  <SortHeader label="Email" sortKey="email" sort={sort} onSort={changeSort} />
                  <th>Industry</th>
                  <th>Status</th>
                  <SortHeader label="Joined" sortKey="created_at" sort={sort} onSort={changeSort} />
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>

              {loading ? (
                <TableSkeleton rows={8} cols={8} />
              ) : (
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className={selected.has(m.id) ? "is-selected" : ""}>
                      <td>
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={selected.has(m.id)}
                          onChange={() => toggleRow(m.id)}
                          aria-label={`Select ${m.name}`}
                        />
                      </td>
                      <td>
                        <div className="row">
                          <Avatar name={m.name} photo={m.featured_photo_url} id={m.id} />
                          <div style={{ minWidth: 0 }}>
                            <div className="cell-strong">{m.name}</div>
                            {m.designation && <div className="cell-muted">{m.designation}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="cell-truncate" title={m.company}>
                        {m.company}
                      </td>
                      <td className="cell-truncate cell-muted" title={m.email || ""}>
                        {m.email || <span style={{ color: "var(--text-faint)" }}>—</span>}
                      </td>
                      <td className="cell-muted">{m.industry || "—"}</td>
                      <td>
                        {m.is_featured ? (
                          <Badge tone="green">
                            <IconStar style={{ width: 11, height: 11 }} />
                            Spotlight
                          </Badge>
                        ) : (
                          <Badge>{m.source}</Badge>
                        )}
                      </td>
                      <td className="cell-muted">{formatDate(m.created_at)}</td>
                      <td className="col-actions">
                        <div className="row" style={{ justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-icon btn-ghost"
                            title={m.is_featured ? "Remove from Spotlight" : "Add to Spotlight"}
                            onClick={() => toggleFeatured(m)}
                          >
                            <IconStar
                              style={{ color: m.is_featured ? "var(--green)" : "var(--text-faint)" }}
                            />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-icon btn-ghost"
                            title="Edit"
                            onClick={() => setEditing(m)}
                          >
                            <IconEdit />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-icon btn-ghost"
                            title="Delete"
                            onClick={() => setConfirming({ type: "one", member: m })}
                          >
                            <IconTrash style={{ color: "var(--red)" }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>

          {!loading && !members.length && (
            <EmptyState
              icon={IconUsers}
              title={hasFilters ? "No members match those filters" : "No members yet"}
              message={
                hasFilters
                  ? "Try loosening the search or clearing a filter."
                  : "Sign-ups from the public join form will appear here."
              }
              action={
                hasFilters ? (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      setFilters(EMPTY_FILTERS);
                      setSearchInput("");
                    }}
                  >
                    Clear filters
                  </button>
                ) : null
              }
            />
          )}

          {!loading && members.length > 0 && (
            <div className="panel-foot">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                pageSize={pagination.pageSize}
                onPage={setPage}
              />
            </div>
          )}
        </div>
      </div>

      {(editing || creating) && (
        <MemberModal
          member={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={(saved, wasNew) => {
            setEditing(null);
            setCreating(false);
            toast.success(wasNew ? `${saved.name} added.` : `${saved.name} updated.`);
            load();
          }}
        />
      )}

      {confirming?.type === "one" && (
        <ConfirmModal
          title={`Delete ${confirming.member.name}?`}
          message="This removes them from the members list permanently. Their event RSVPs are kept."
          onConfirm={() => deleteOne(confirming.member)}
          onClose={() => setConfirming(null)}
          busy={busy}
        />
      )}

      {confirming?.type === "bulk" && (
        <ConfirmModal
          title={`Delete ${selected.size.toLocaleString()} members?`}
          message="This permanently removes every selected member. This can't be undone."
          confirmLabel={`Delete ${selected.size.toLocaleString()}`}
          onConfirm={() => runBulk("delete")}
          onClose={() => setConfirming(null)}
          busy={busy}
        />
      )}
    </>
  );
}

function SortHeader({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th className="sortable" onClick={() => onSort(sortKey)}>
      {label}
      {active && <span className="sort-arrow">{sort.direction === "asc" ? "↑" : "↓"}</span>}
    </th>
  );
}

const BLANK = {
  name: "",
  designation: "",
  company: "",
  email: "",
  industry: "",
  linkedin_url: "",
  location: "",
  notes: "",
};

function MemberModal({ member, onClose, onSaved }) {
  const isNew = !member;
  const [form, setForm] = useState(() =>
    isNew
      ? BLANK
      : Object.fromEntries(Object.keys(BLANK).map((k) => [k, member[k] ?? ""]))
  );
  const [fields, setFields] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setFields({});

    try {
      const res = isNew ? await api.post("/members", form) : await api.patch(`/members/${member.id}`, form);
      onSaved(res.member, isNew);
    } catch (err) {
      setError(err.message);
      setFields(err.fields || {});
      setBusy(false);
    }
  }

  return (
    <Modal
      title={isNew ? "Add a member" : `Edit ${member.name}`}
      subtitle={isNew ? "Add someone to the Circle by hand." : "Changes show on the public site immediately."}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="member-form" className="btn btn-primary" disabled={busy}>
            {busy && <Spinner />}
            {isNew ? "Add member" : "Save changes"}
          </button>
        </>
      }
    >
      <form id="member-form" onSubmit={submit} noValidate>
        {error && (
          <div className="login-alert" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div className="field-grid">
          <Field label="Full name *" error={fields.name}>
            <input className={`input ${fields.name ? "has-error" : ""}`} value={form.name} onChange={set("name")} required />
          </Field>
          <Field label="Designation" error={fields.designation}>
            <input className="input" value={form.designation} onChange={set("designation")} placeholder="e.g. HR Director" />
          </Field>
          <Field label="Company *" error={fields.company}>
            <input className={`input ${fields.company ? "has-error" : ""}`} value={form.company} onChange={set("company")} required />
          </Field>
          <Field label="Industry" error={fields.industry}>
            <input className="input" value={form.industry} onChange={set("industry")} placeholder="e.g. Retail, Finance" />
          </Field>
          <Field label="Email" error={fields.email} hint="Optional — imported members often have none.">
            <input className={`input ${fields.email ? "has-error" : ""}`} type="email" value={form.email} onChange={set("email")} />
          </Field>
          <Field label="Location" error={fields.location}>
            <input className="input" value={form.location} onChange={set("location")} placeholder="e.g. Dubai" />
          </Field>
        </div>

        <Field label="LinkedIn profile" error={fields.linkedin_url}>
          <input
            className={`input ${fields.linkedin_url ? "has-error" : ""}`}
            type="url"
            value={form.linkedin_url}
            onChange={set("linkedin_url")}
            placeholder="https://linkedin.com/in/…"
          />
        </Field>

        <Field label="Internal notes" hint="Only ever visible here in the admin panel.">
          <textarea className="textarea" value={form.notes} onChange={set("notes")} rows={3} />
        </Field>
      </form>
    </Modal>
  );
}
